/** Court case normalisation and fallback helpers */

export const getCaseDocket = (item) =>
  item?.docket_number ||
  item?.case_reference ||
  item?.reference ||
  item?.docket ||
  '';

export const getCaseTitle = (item) =>
  item?.title || item?.case_name || item?.name || '';

export const normalizeCourtCase = (item) => {
  const docket = getCaseDocket(item);
  return {
    id: item?.id || item?.case_id || docket,
    docket_number: docket,
    title: getCaseTitle(item) || docket || 'Untitled case',
    status: item?.status || item?.case_status || 'active',
    court_name: item?.court_name || item?.court || '',
    created_at: item?.created_at || item?.filed_at || item?.date || null,
    updated_at: item?.updated_at || null,
    description: item?.description || item?.summary || '',
    transcription_count: item?.transcription_count ?? item?.session_count ?? null,
  };
};

export const caseRouteId = (courtCase) => {
  const id = courtCase?.id ?? courtCase?.docket_number;
  return encodeURIComponent(String(id));
};

export const caseDetailPath = (courtCaseOrId) => {
  if (typeof courtCaseOrId === 'object' && courtCaseOrId !== null) {
    return `/court-cases/${caseRouteId(courtCaseOrId)}`;
  }
  return `/court-cases/${encodeURIComponent(String(courtCaseOrId))}`;
};

/** Build synthetic case list from transcription records when court API is unavailable */
export const buildCasesFromTranscriptions = (transcriptions) => {
  const byDocket = new Map();

  transcriptions.forEach((item) => {
    const docket =
      item.case_reference ||
      item.caseReference ||
      item.metadata?.case_reference ||
      item.docket_number;

    if (!docket) return;

    const existing = byDocket.get(docket);
    const createdAt = item.created_at || item.date || item.uploaded_at;

    if (!existing) {
      byDocket.set(docket, normalizeCourtCase({
        id: docket,
        docket_number: docket,
        title: item.case_title || item.title || docket,
        status: 'active',
        created_at: createdAt,
        transcription_count: 1,
      }));
      return;
    }

    existing.transcription_count = (existing.transcription_count || 0) + 1;
    if (createdAt && (!existing.created_at || new Date(createdAt) < new Date(existing.created_at))) {
      existing.created_at = createdAt;
    }
  });

  return Array.from(byDocket.values()).sort((a, b) => {
    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return dateB - dateA;
  });
};

export const filterCourtCases = (cases, { search = '', status = 'all', dateFrom = '', dateTo = '' } = {}) => {
  let result = [...cases];
  const needle = search.trim().toLowerCase();

  if (needle) {
    result = result.filter(
      (c) =>
        c.docket_number.toLowerCase().includes(needle) ||
        c.title.toLowerCase().includes(needle)
    );
  }

  if (status !== 'all') {
    result = result.filter((c) => (c.status || 'active').toLowerCase() === status.toLowerCase());
  }

  if (dateFrom) {
    const from = new Date(dateFrom).getTime();
    result = result.filter((c) => !c.created_at || new Date(c.created_at).getTime() >= from);
  }

  if (dateTo) {
    const to = new Date(dateTo).getTime() + 86400000;
    result = result.filter((c) => !c.created_at || new Date(c.created_at).getTime() < to);
  }

  return result;
};

export const findCaseByRouteId = (cases, routeId) => {
  const decoded = decodeURIComponent(routeId);
  return cases.find(
    (c) =>
      String(c.id) === decoded ||
      c.docket_number === decoded ||
      encodeURIComponent(String(c.id)) === routeId
  );
};

export const transcriptionMatchesCase = (transcription, courtCase) => {
  const ref =
    transcription.case_reference ||
    transcription.caseReference ||
    transcription.metadata?.case_reference ||
    transcription.docket_number;

  if (!ref) return false;
  return (
    ref === courtCase.docket_number ||
    String(ref) === String(courtCase.id)
  );
};
